import getFileExtension from 'utils/getFileExtension';

/** The 3D formats the model viewer opens, one loader each (loadModel.js). */
export const MODEL_FORMATS = Object.freeze({
  IFC: 'ifc',
  GLTF: 'gltf',
  OBJ: 'obj',
  STL: 'stl',
  FBX: 'fbx',
});

const FORMAT_BY_EXTENSION = Object.freeze({
  ifc: MODEL_FORMATS.IFC,
  glb: MODEL_FORMATS.GLTF,
  gltf: MODEL_FORMATS.GLTF,
  obj: MODEL_FORMATS.OBJ,
  stl: MODEL_FORMATS.STL,
  fbx: MODEL_FORMATS.FBX,
});

/** File extensions (lower case, without the dot) the model viewer opens. */
export const MODEL_EXTENSIONS = Object.freeze(Object.keys(FORMAT_BY_EXTENSION));

/**
 * CAD formats the viewer cannot draw: DWG is closed, and its free readers
 * are GPL. The viewer opens these to say how to export the model as IFC or
 * FBX instead, and still offers the file for download.
 */
export const EXPORT_ONLY_EXTENSIONS = Object.freeze(['dwg']);

/** Every extension the model viewer dialog opens, for the bank's icons. */
export const MODEL_VIEWER_EXTENSIONS = Object.freeze([
  ...MODEL_EXTENSIONS,
  ...EXPORT_ONLY_EXTENSIONS,
]);

const extensionOf = (name) =>
  typeof name === 'string' && name.includes('.')
    ? getFileExtension(name).toLowerCase()
    : null;

/**
 * The model format of a file by its name's extension, or null when the
 * viewer cannot open it (no extension, or another format).
 * @param {string} name file name, e.g. `Talo A.ifc`
 * @returns {string|null} one of MODEL_FORMATS
 */
export function getModelFormat(name) {
  const extension = extensionOf(name);
  return (extension && FORMAT_BY_EXTENSION[extension]) || null;
}

/**
 * Whether the model viewer can open a file of this name.
 * @param {string} name
 * @returns {boolean}
 */
export const isModelFile = (name) => getModelFormat(name) !== null;

/**
 * Whether a file is a CAD model the viewer explains how to export
 * (EXPORT_ONLY_EXTENSIONS) rather than draws.
 * @param {string} name
 * @returns {boolean}
 */
export const isExportOnlyModelFile = (name) =>
  EXPORT_ONLY_EXTENSIONS.includes(extensionOf(name));

/**
 * Whether opening a bank file shows the model viewer dialog: a model it
 * draws, or a DWG it explains.
 * @param {string} name
 * @returns {boolean}
 */
export const opensInModelViewer = (name) =>
  isModelFile(name) || isExportOnlyModelFile(name);
